import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { AdminActions } from "src/redux/Admin/actions";
import { formatKst } from "src/utils/formatKst";
import Button from "../../common/Button";
import styles from "./styles.module.scss";

// Date <-> "YYYY-MM-DD" string (the format the audit filter sends + the picker shows).
const pad = (n) => String(n).padStart(2, "0");
const toYMD = (d) => (d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "");
const parseYMD = (s) => (s ? new Date(`${s}T00:00:00`) : null);

// The audit feed is platform-wide and generic: each entry is one admin change
// with action / who / when / why, and a details object holding any scope (a cpty
// code, a flag name, a setting key) plus the change breakdown.
const PAGE_SIZE = 50;

// The stored action is an ALL_CAPS code (kept as a small tag); the Action column
// shows a human-readable label. Unknown codes fall back to a humanized form.
const ACTION_LABELS = {
    TOGGLE_FLAG: "Toggled flag",
    TOGGLE_FLAGS: "Toggled flags",
    SET_FLAG_OVERRIDE: "Set flag override",
    REMOVE_FLAG_OVERRIDE: "Removed flag override",
    SET_FLAG_GROUP_OVERRIDES: "Set flag group overrides",
    SET_HFS_ENABLED: "Set HFS enabled",
    SET_HFS_API_KEY: "Set HFS API key",
    SET_MARKET_DATA_PROVIDER: "Set market-data provider",
    SET_MARKET_DATA_SCHEDULE: "Set market-data schedule",
    DELETE_MARKET_DATA_SCHEDULE: "Deleted market-data schedule",
    SET_CPTY_USER_ROLE: "Set counterparty user role",
    ENABLE_USER: "Enabled user",
    DISABLE_USER: "Disabled user",
    DELETE_USER: "Deleted user",
    SETUP_OWN_CPTY_SUBMITTED: "Onboarded counterparty",
    EDIT_CPTY_CONFIG: "Edited counterparty config",
    EDIT_CPTY_DIRECTORY: "Edited counterparty directory",
    EDIT_CPTY_API_CONFIG: "Edited counterparty API config",
    CANCEL_CPTY_OFFBOARD: "Cancelled counterparty off-boarding",
    BATCH_SETUP_SUBMITTED: "Batch-onboarded counterparties",
    UPDATE_API_KEY: "Updated counterparty API key",
    OFFBOARD_CPTIES: "Off-boarded counterparties",
    OFFBOARD_CPTY_GROUPS: "Off-boarded counterparty groups",
    REGISTER_BROKER: "Registered broker",
    DELETE_BROKER: "Deleted broker",
    SECURITY_BAN_ADD: "Added IP ban",
    SECURITY_BAN_REMOVE: "Removed IP ban",
    SECURITY_ALLOW_ADD: "Added allowlist entry",
    SECURITY_ALLOW_REMOVE: "Removed allowlist entry",
};

// "FOO_BAR_BAZ" -> "Foo bar baz".
const humanizeAction = (a) =>
    a.toLowerCase().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const actionLabel = (a) => (a ? ACTION_LABELS[a] || humanizeAction(a) : "-");

const Audit = () => {
    const dispatch = useDispatch();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const offsetRef = useRef(0);
    const loadingRef = useRef(false);
    const sentinelRef = useRef(null);

    // Fetch a page and append it (gradual loading). `reset` starts a fresh feed
    // from offset 0 (initial load or a filter change).
    const load = async (reset, range = { from, to }) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const offset = reset ? 0 : offsetRef.current;
            const resp = await dispatch(AdminActions.FetchAuditLog({
                limit: PAGE_SIZE,
                offset,
                from: range.from || undefined,
                to: range.to || undefined,
            }));
            const pageRows = Array.isArray(resp) ? resp : [];
            offsetRef.current = offset + pageRows.length;
            setHasMore(pageRows.length === PAGE_SIZE);
            setEntries((prev) => (reset ? pageRows : [...prev, ...pageRows]));
        } catch (e) {
            console.error("Failed to load audit log:", e);
            setError(e.message || "Failed to load audit log");
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    };

    // A date-filter change (or Refresh) restarts the feed from the top.
    const applyFilter = () => load(true, { from, to });
    const clearFilter = () => { setFrom(""); setTo(""); load(true, { from: "", to: "" }); };
    const refresh = () => load(true, { from, to });

    useEffect(() => {
        load(true, { from: "", to: "" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the next page when the bottom sentinel scrolls into view.
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const obs = new IntersectionObserver((es) => {
            if (es[0].isIntersecting && hasMore && !loadingRef.current) load(false);
        }, { rootMargin: "200px" });
        obs.observe(el);
        return () => obs.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, from, to, entries.length]);

    const initialLoading = loading && entries.length === 0;

    return (
        <div className={styles.audit}>
            <div className={styles.header}>
                <div className={styles.title}>Audit</div>
                <div className={styles.controls}>
                    <div className={styles.dateFilter}>
                        <span>From</span>
                        <DatePicker
                            selected={parseYMD(from)}
                            onChange={(d) => setFrom(toYMD(d))}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="YYYY-MM-DD"
                            isClearable
                            className={styles.dateInput}
                        />
                    </div>
                    <div className={styles.dateFilter}>
                        <span>To</span>
                        <DatePicker
                            selected={parseYMD(to)}
                            onChange={(d) => setTo(toYMD(d))}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="YYYY-MM-DD"
                            isClearable
                            className={styles.dateInput}
                        />
                    </div>
                    <Button value="Apply" dataTestId="audit-apply" onClick={applyFilter} classes={[styles.refreshBtn]} />
                    {(from || to) && (
                        <Button value="Clear" dataTestId="audit-clear" onClick={clearFilter} classes={[styles.refreshBtn]} />
                    )}
                    <Button value="Refresh" dataTestId="audit-refresh" onClick={refresh} classes={[styles.refreshBtn]} />
                </div>
            </div>

            {error && <div className={styles.error}>{error}</div>}
            {initialLoading && <div className={styles.loading}>Loading...</div>}

            {!initialLoading && !error && entries.length === 0 && (
                <div className={styles.empty}>No audit entries yet.</div>
            )}

            {entries.length > 0 && (
                <table className={styles.table} data-testid="audit-table">
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Action</th>
                            <th>By</th>
                            <th>Reason</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((e, i) => (
                            <tr key={i}>
                                <td className={styles.when}>{formatWhen(e.timestamp)}</td>
                                <td className={styles.action}>{actionLabel(e.action)}</td>
                                <td>{e.user || "-"}</td>
                                <td className={styles.reason}>{e.reason || "-"}</td>
                                <td className={styles.detailsCell}>
                                    <Button
                                        value="Details"
                                        dataTestId={`audit-details-${i}`}
                                        onClick={() => setSelected(e)}
                                        classes={[styles.detailsBtn]}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {entries.length > 0 && (
                <div ref={sentinelRef} className={styles.loadMore} data-testid="audit-loadmore">
                    {loading && !initialLoading ? "Loading more…" : !hasMore ? "End of log" : ""}
                </div>
            )}

            {selected && (
                <DetailsDialog entry={selected} onClose={() => setSelected(null)} />
            )}
        </div>
    );
};

const DetailsDialog = ({ entry, onClose }) => {
    const rows = useMemo(() => flatten(entry.details), [entry]);
    return (
        <div className={styles.dialogWrapper} onClick={onClose}>
            <div className={styles.dialog} onClick={(ev) => ev.stopPropagation()}>
                <div className={styles.dialogHeader}>
                    <div className={styles.dialogTitle}>{entry.action ? actionLabel(entry.action) : "Change"}</div>
                    <div className={styles.close} onClick={onClose} data-testid="audit-details-close" />
                </div>
                <div className={styles.dialogMeta}>
                    <div><span>When</span>{formatWhen(entry.timestamp)}</div>
                    <div><span>By</span>{entry.user || "-"}</div>
                    <div><span>Reason</span>{entry.reason || "-"}</div>
                </div>
                <div className={styles.dialogBody}>
                    {rows.length === 0 ? (
                        <div className={styles.empty}>No further detail.</div>
                    ) : (
                        <table className={styles.detailsTable}>
                            <tbody>
                                {rows.map(({ key, value }, i) => (
                                    <tr key={i}>
                                        <td className={styles.detailKey}>{key}</td>
                                        <td className={styles.detailValue}>{value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

// ISO-8601 timestamp (stored as Instant.toString()) -> YYYY-MM-DD hh:mm:ss KST.
const formatWhen = (ts) => formatKst(ts, "-");

// Flatten a details object into label/value rows for the dialog. Nested
// objects/arrays are shown as compact JSON so nothing is lost.
const flatten = (details) => {
    if (details == null) return [];
    if (typeof details !== "object") {
        return [{ key: "value", value: String(details) }];
    }
    return Object.entries(details).map(([key, value]) => ({
        key,
        value:
            value != null && typeof value === "object"
                ? JSON.stringify(value)
                : String(value),
    }));
};

export default Audit;
